import Typography from '@mui/material/Typography';
import {
	DataGrid,
	GridToolbar,
	GridFooter,
	GridFooterContainer,
} from '@mui/x-data-grid';
import { useMemo } from 'react';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import FormHelperText from '@mui/material/FormHelperText';
import dayjs from 'dayjs';
import Stack from '@mui/material/Stack';
import CustomLoading from '../../shared/CustomLoading';
import { formatPhone } from '../../../scripts/phoneNumberHelper';

const CenteredCell = ({ children }) => (
	<Stack justifyContent='center' sx={{ height: '100%', py: 2 }}>
		{children}
	</Stack>
);

CenteredCell.propTypes = {
	children: PropTypes.node,
};

const Footer = ({
	inverse = false,
	selectionModel = [],
	allChecked = false,
	totalRows = 0,
}) => {
	const renderText = () => {
		if (!selectionModel?.length && !allChecked) {
			return null;
		}
		if (inverse) {
			return `Clinic(s) to be removed: ${selectionModel.length}`;
		}
		if (allChecked) {
			return `Clinic(s) to be added: ${totalRows}`;
		}
		return `Clinic(s) to be added: ${selectionModel.length}`;
	};

	return (
		<GridFooterContainer>
			<div style={{ margin: '0 16px' }}>{renderText()}</div>
			<GridFooter />
		</GridFooterContainer>
	);
};

Footer.propTypes = {
	inverse: PropTypes.bool,
	selectionModel: PropTypes.array,
	allChecked: PropTypes.bool,
	totalRows: PropTypes.number,
};

const SubAccountClinicsTable = ({
	isLoading,
	pageTitle,
	clinics,
	totalRows,
	error = undefined,
	inverse = false,
	locked = false,
	allChecked = false,
	selectionModel,
	selectionModelUpdate,
	page,
	onPageChange,
	pageSize,
	onPageSizeChange,
	onSearchStrChange,
}) => {
	// Stable paginationModel reference for DataGrid v8 controlled pagination
	const paginationModel = useMemo(
		() => ({ page, pageSize }),
		[page, pageSize]
	);

	// v8 rowSelectionModel must be { type: 'include'|'exclude', ids: Set<GridRowId> }
	// (v5 accepted a plain array; v8 does not — passing an array crashes GridFooter)
	const v8SelectionModel = useMemo(() => {
		if (locked && allChecked) {
			// All boxes checked and locked — select all via exclude-nothing
			return { type: 'exclude', ids: new Set() };
		}
		const allRowIds = clinics.map(row => row.drFedID);
		if (inverse) {
			// Inverse: show all as checked EXCEPT those in selectionModel (exclusion list)
			const exclusionSet = new Set(selectionModel);
			return {
				type: 'include',
				ids: new Set(allRowIds.filter(id => !exclusionSet.has(id))),
			};
		}
		return { type: 'include', ids: new Set(selectionModel) };
	}, [clinics, locked, allChecked, inverse, selectionModel]);

	const columns = [
		{
			field: 'drFedID',
			renderHeader: () => (
				<Typography variant='body2' fontWeight='fontWeightBold'>
					Id
				</Typography>
			),
		},
		{
			field: 'clinicName',
			flex: 1,
			minWidth: 300,
			renderHeader: () => (
				<Typography variant='body2' fontWeight='fontWeightBold'>
					Clinics
				</Typography>
			),
			renderCell: params => (
				<CenteredCell>{params.formattedValue}</CenteredCell>
			),
		},
		{
			field: 'address',
			flex: 1,
			minWidth: 300,
			renderHeader: () => (
				<Typography variant='body2' fontWeight='fontWeightBold'>
					Address
				</Typography>
			),
			renderCell: params => (
				<CenteredCell>
					<div> {params.row.address1}</div>
					<div> {params.row.address2}</div>
					<div>
						{params.row.city}, {params.row.state}{' '}
						{params.row.zipCode}
					</div>
				</CenteredCell>
			),
		},
		{
			field: 'info',
			renderHeader: () => (
				<Typography variant='body2' fontWeight='fontWeightBold'>
					Info
				</Typography>
			),
			width: 200,
			renderCell: params => (
				<CenteredCell>
					<div>
						<strong>Phone: </strong>
						{formatPhone(params.row.phone) || 'N/A'}
					</div>
					<div>
						<strong>Fax: </strong>
						{formatPhone(params.row.fax) || 'N/A'}
					</div>
					<div>
						<strong>Tax ID #: </strong>
						{params.row.taxID}
					</div>
				</CenteredCell>
			),
		},
		{
			field: 'drEffectiveDate',
			width: 120,
			renderHeader: () => (
				<Typography variant='body2' fontWeight='fontWeightBold'>
					Effective
				</Typography>
			),
			valueFormatter: value => {
				if (!value) return '';
				return dayjs(value).format('MM/DD/YYYY');
			},
			renderCell: params => (
				<CenteredCell>{params.formattedValue}</CenteredCell>
			),
		},
		{
			field: 'drTerminationDate',
			width: 120,
			renderHeader: () => (
				<Typography variant='body2' fontWeight='fontWeightBold'>
					Termed
				</Typography>
			),
			valueFormatter: value => {
				if (!value) return '';
				return dayjs(value).format('MM/DD/YYYY');
			},
			renderCell: params => (
				<CenteredCell>{params.formattedValue}</CenteredCell>
			),
		},
	];

	return (
		<Box
			sx={{
				mt: 6,
			}}
		>
			<Typography variant='h6' component='label' sx={{ mb: 1 }}>
				{pageTitle}
			</Typography>
			<DataGrid
				rows={clinics}
				rowCount={totalRows}
				getRowId={row => row.drFedID}
				showToolbar
				autoHeight
				pagination
				disableColumnMenu
				disableColumnFilter
				disableColumnResize
				disableColumnSelector
				disableDensitySelector
				checkboxSelection
				isRowSelectable={() => !locked}
				keepNonExistentRowsSelected
				hideFooterSelectedRowCount
				getRowHeight={() => 'auto'}
				getEstimatedRowHeight={() => 100}
				pageSizeOptions={[10, 20, 50]}
				columns={columns}
				rowSelectionModel={v8SelectionModel}
				paginationMode='server'
				paginationModel={paginationModel}
				onPaginationModelChange={newModel => {
					if (newModel.page !== page) onPageChange(newModel.page);
					if (newModel.pageSize !== pageSize)
						onPageSizeChange(newModel.pageSize);
				}}
				loading={isLoading}
				onRowSelectionModelChange={newModel => {
					if (locked && allChecked) return;
					if (inverse) {
						// Inverse: unchecked rows on current page = rows to remove
						const currentPageIds = new Set(
							clinics.map(r => r.drFedID)
						);
						const unchecked = clinics
							.map(r => r.drFedID)
							.filter(id => !newModel.ids.has(id));
						// Preserve exclusions from other pages
						const prevOtherPages = selectionModel.filter(
							id => !currentPageIds.has(id)
						);
						selectionModelUpdate([...prevOtherPages, ...unchecked]);
					} else {
						// newModel.ids contains all selected IDs across pages
						selectionModelUpdate(Array.from(newModel.ids));
					}
				}}
				slots={{
					toolbar: GridToolbar,
					loadingOverlay: CustomLoading,
					footer: Footer,
				}}
				slotProps={{
					toolbar: {
						showQuickFilter: true,
						printOptions: {
							disableToolbarButton: true,
						},
						csvOptions: { disableToolbarButton: true },
						quickFilterProps: { debounceMs: 300 },
					},
					footer: {
						inverse,
						selectionModel,
						allChecked,
						totalRows,
					},
					loadingOverlay: {
						variant: 'skeleton',
						noRowsVariant: 'skeleton',
						'data-testid': 'datagridLoadingOverlay',
					},
				}}
				sx={{
					'.MuiDataGrid-columnHeaderTitleContainer': {
						'.MuiSvgIcon-root': {
							color: 'white',
						},
					},
				}}
				columnVisibilityModel={{
					drFedID: false,
				}}
				filterMode='server'
				onFilterModelChange={newFilterModel => {
					onSearchStrChange(
						newFilterModel.quickFilterValues.join(' ')
					);
				}}
			/>
			{error && <FormHelperText error>{error}</FormHelperText>}
		</Box>
	);
};

SubAccountClinicsTable.propTypes = {
	isLoading: PropTypes.bool.isRequired,
	locked: PropTypes.bool,
	allChecked: PropTypes.bool,
	inverse: PropTypes.bool,
	pageTitle: PropTypes.string.isRequired,
	clinics: PropTypes.arrayOf(PropTypes.object).isRequired,
	error: PropTypes.string,
	selectionModel: PropTypes.arrayOf(PropTypes.object).isRequired,
	selectionModelUpdate: PropTypes.func.isRequired,
	page: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	onPageSizeChange: PropTypes.func.isRequired,
	totalRows: PropTypes.number.isRequired,
	onSearchStrChange: PropTypes.func.isRequired,
};

export default SubAccountClinicsTable;
